import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { ChevronDown, ChevronRight } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FAQPage = () => {
  const { t, lang } = useLanguage();
  const [items, setItems] = useState([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const [openId, setOpenId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/faqs`);
      setItems(response.data);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error("Error fetching FAQ:", error);
    } finally {
      setLoading(false);
    }
  };

  const categories = ["all", "general", "ordering", "delivery", "quality"];

  const filteredItems = activeCategory === "all" 
    ? items 
    : items.filter(item => item.category === activeCategory);

  // Placeholder FAQs
  const placeholderFAQs = [
    { id: "1", question_sq: "Cili është minimumi i porosisë?", question_en: "What is the minimum order?", answer_sq: "Minimumi i porosisë varet nga produkti dhe mund të diskutohet sipas nevojave tuaja specifike. Na kontaktoni për më shumë detaje.", answer_en: "The minimum order depends on the product and can be discussed according to your specific needs. Contact us for more details.", category: "ordering" },
    { id: "2", question_sq: "Si garantoni cilësinë e produkteve?", question_en: "How do you guarantee product quality?", answer_sq: "Operojmë sipas standardeve HACCP dhe kemi certifikime ndërkombëtare. Çdo produkt është i gjurmueshëm 100% nga origjina deri te dorëzimi.", answer_en: "We operate according to HACCP standards and have international certifications. Every product is 100% traceable from origin to delivery.", category: "quality" },
    { id: "3", question_sq: "A ofroni transport të ftohur?", question_en: "Do you offer refrigerated transport?", answer_sq: "Po, kemi flotë të specializuar me transport të ftohur për të garantuar freskinë maksimale të produkteve gjatë dorëzimit.", answer_en: "Yes, we have a specialized fleet with refrigerated transport to guarantee maximum freshness of products during delivery.", category: "delivery" },
    { id: "4", question_sq: "Cilat zona i mbuloni me dërgesë?", question_en: "Which areas do you cover for delivery?", answer_sq: "Mbulojmë të gjithë Shqipërinë me dërgesë të drejtpërdrejtë. Për zona të tjera, na kontaktoni për të diskutuar opsionet.", answer_en: "We cover all of Albania with direct delivery. For other areas, contact us to discuss options.", category: "delivery" },
    { id: "5", question_sq: "A mund të personalizoj formatin e karkasës?", question_en: "Can I customize the carcass format?", answer_sq: "Po, ofrojmë fleksibilitet të plotë. Mund të porosisni karkasë të plotë, gjysmë karkasa, pa shpatulla, ose sipas specifikimeve tuaja.", answer_en: "Yes, we offer full flexibility. You can order full carcass, half carcass, without shoulders, or according to your specifications.", category: "ordering" }
  ];

  const displayItems = items.length > 0 ? filteredItems : (activeCategory === "all" ? placeholderFAQs : placeholderFAQs.filter(f => f.category === activeCategory));

  return (
    <div className="pt-20" data-testid="faq-page">
      {/* Header */}
      <section className="py-24 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-4">{t.nav.faq}</p>
          <h1 className="text-5xl sm:text-6xl font-black tracking-tighter text-[#0A0A0A] mb-6" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            {t.faq.title}
          </h1>
          <p className="text-xl text-zinc-600 max-w-2xl">{t.faq.subtitle}</p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-white border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  activeCategory === cat 
                    ? "bg-[#0A0A0A] text-white" 
                    : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"
                }`}
              >
                {t.faq.categories[cat]}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ List */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-12 text-zinc-500">{t.common.loading}</div>
          ) : (
            <div className="space-y-px">
              {displayItems.map((item) => (
                <div key={item.id} className="border border-zinc-200" data-testid="faq-item">
                  <button
                    onClick={() => setOpenId(openId === item.id ? null : item.id)}
                    className="w-full flex items-center justify-between p-6 text-left hover:bg-zinc-50 transition-colors"
                  >
                    <span className="font-medium text-[#0A0A0A] pr-4">
                      {lang === "sq" ? item.question_sq : item.question_en}
                    </span>
                    <ChevronDown className={`w-5 h-5 text-zinc-500 transition-transform ${openId === item.id ? "rotate-180" : ""}`} />
                  </button>
                  {openId === item.id && (
                    <div className="px-6 pb-6 text-zinc-600">
                      {lang === "sq" ? item.answer_sq : item.answer_en}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Contact CTA */}
          <div className="mt-16 text-center border border-zinc-200 p-8">
            <p className="text-zinc-600 mb-4">{t.faq.contact}</p>
            <Link 
              to="/contact" 
              className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-6 py-3 font-medium hover:bg-zinc-800 transition-colors"
            >
              {t.nav.contact} <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
